import { StrictMode, startTransition } from 'react';
import { hydrateRoot } from 'react-dom/client';
import { StartClient } from '@tanstack/react-start/client';
import { errorReportData, extractErrorInfo, logErrorFn } from './lib/data';
import { isHydrationError, shouldReportFrontendError } from './lib/frontend-error-filter';
import { describeHydrationRewrite, markHydrationStarted } from './lib/hydration-rewrite-monitor';
import { clientQueryClient } from './router';
import { recentBreadcrumbs } from './lib/session-actions';
import { isVersionSkewReloadInProgress } from './routes/-components/default-catch-boundary';
import { COUNTER_LOCALE_ATTR, COUNTER_MODE_ATTR, COUNTER_VALUE_ATTR } from '@worldcoo/ui/lib/counter-contract';

/**
 * Custom client entry, auto-detected as `src/client` and replacing the package default (see
 * `resolveEntry` in @tanstack/start-plugin-core). It exists for exactly one reason: to pass React's
 * error hooks — `onRecoverableError` and `onUncaughtError` — to `hydrateRoot`, so a hydration failure
 * is reported with the `componentStack` that names our own components instead of only React internals.
 *
 * Everything else is a verbatim copy of the default entry — `startTransition`, `StrictMode`, hydrating
 * `document`. Keep it that way, or hydration behaviour drifts from upstream for no reason.
 */

// `isHydrationError` lives in `frontend-error-filter.ts` so the global handlers in `router.tsx`
// can apply the same translated-document gate to the hydration errors that surface there.

/**
 * The two numbers a production React #418 refuses to name: it reports that a text node disagreed, never
 * which text. Pairing each counter's `data-value` (the CLIENT's figure) with the text still in the DOM
 * (what the SERVER wrote) names the divergence — `instant/collected:0=>6,699.00 €` is a client that
 * resolved no aggregate, `instant/collected:6699=>6.699,00 €` would be a locale regression.
 *
 * Only `instant` counters render a live figure on the first pass, so only those lines can show a real
 * mismatch; hence the mode prefix. Travels in its OWN report field, never the message or stack (the
 * reporter fingerprints on those, and per-occurrence numbers there would mint a ticket per figure) and
 * never as a breadcrumb (a 21st breadcrumb once tripped the array bound and voided the whole report).
 *
 * Fully guarded, like `describeCauseChain`: this runs inside `onRecoverableError` while the DOM is in
 * whatever state the failed hydration left it, and failing to enrich a report must never cost the report.
 */
function counterSnapshot(): string | undefined {
	if (typeof document === 'undefined') return undefined;
	try {
		// Skip React's streamed-Suspense holding containers (`<div hidden id="S:n">`). When a boundary is
		// client-rendered rather than hydrated, its holder is left in the document with the SERVER's copy of
		// the subtree still inside — on a campaign page that is roughly half the DOM and a duplicate of every
		// counter. Reading those would report stale figures as if they were live.
		const nodes = Array.from(document.querySelectorAll<HTMLElement>(`[${COUNTER_VALUE_ATTR}]`))
			.filter((el) => !el.closest('div[hidden][id^="S:"]'))
			.slice(0, 10);
		if (nodes.length === 0) return undefined;
		// The locale attribute names the side that formatted the node: React never patches attributes on
		// hydration, so a hydrated node keeps the SERVER's render locale while a client-rendered node
		// carries the CLIENT's — read together with the text, it says which render used the wrong locale
		// (the open attribution question in the TEC-3696 #418 family). `document.documentElement.lang` is
		// the shell's locale, prefixed once for comparison.
		const shellLocale = document.documentElement.lang || '?';
		const lines = nodes.map(
			(el) =>
				`${el.getAttribute(COUNTER_MODE_ATTR) ?? '?'}/${el.getAttribute(COUNTER_LOCALE_ATTR) ?? '?'}/${el.dataset.testid || '-'}:${el.getAttribute(COUNTER_VALUE_ATTR) ?? '?'}=>${(el.textContent ?? '').trim().slice(0, 24)}`,
		);
		return [`lang:${shellLocale}`, ...lines].join(' | ');
	} catch {
		return undefined;
	}
}

/**
 * Report the errors React *recovers* from, above all the hydration mismatch behind the
 * `PORTAL_FRONTEND_ERROR: … Minified React error #418` tickets (TEC-3651, TEC-3655).
 *
 * Those tickets are undiagnosable as reported: in a production build the message is `Minified React
 * error #418` and the only stack is React's own internals inside `vendor-react-*.js`, so nothing says
 * WHICH text in WHICH component. `onRecoverableError` is the only place the missing detail exists —
 * React hands it an `errorInfo` carrying a `componentStack`, and every chunk ships a sourcemap, so those
 * frames resolve to real component names.
 *
 * Why this does not double-report: React's DEFAULT `onRecoverableError` calls `reportError(error)`,
 * which dispatches a window `error` event — what the global handler in `router.tsx` has been picking up
 * all along. Supplying our own replaces that call, so each recoverable error is reported once, here,
 * with the component stack. The `console.error` preserves the visibility `reportError` used to give.
 *
 * Scope worth knowing: `StartClient` renders the router behind an `Await`, so the app subtree is
 * client-rendered rather than hydrated and a divergence inside it raises nothing. What this catches is
 * the hydrated shell — the `<html>`/`<body>` tree in `__root.tsx` — which is where the production #418s
 * have to be coming from.
 *
 * The component stack rides in `stack`, never in `message`: the system-error reporter fingerprints on
 * the normalized message, so a per-occurrence component path there would mint a fresh ticket for every
 * distinct component and bury the board.
 */
function reportWithComponentStack(
	resolvePrefix: (message: string) => string,
	error: unknown,
	errorInfo: { componentStack?: string | null },
): void {
	// A stale-chunk reload is already in flight; whatever React recovered from is a symptom of the
	// version skew, not a bug. Same guard as the global handlers in `router.tsx`.
	if (isVersionSkewReloadInProgress()) return;

	const info = extractErrorInfo(error);
	const prefix = resolvePrefix(info.message);
	console.error(prefix, info.name, info.message, errorInfo.componentStack);

	// A hydration mismatch on a document something else already rewrote is that rewriter's doing, not
	// ours. `describeHydrationRewrite` owns both signals and is asked by every report path, here and in
	// `router.tsx`, so no path can be left behind on a weaker gate. It fails open, and the console.error
	// above fires either way, so the recovery stays visible in the browser.
	const rewrite = isHydrationError(info.message) ? describeHydrationRewrite(clientQueryClient()) : undefined;
	if (rewrite?.foreign) return;

	// Environmental noise reaches this path too, so it goes through the same filter as everything else.
	if (!shouldReportFrontendError(info.name, info.message, info.stack, info.cause)) return;

	const componentStack = errorInfo.componentStack?.trim();
	logErrorFn({
		data: errorReportData(prefix + ' ', info, {
			stack: componentStack ? `${info.stack ?? '(no stack)'}\n\nComponent stack:${componentStack}` : info.stack,
			// Same shape the boundary and the two global handlers send, so this path is not the odd one out
			// in telemetry. Breadcrumbs are usually near-empty here (a mismatch happens during load, before
			// the visitor has done anything), which is itself a useful signal.
			source: 'recoverable',
			breadcrumbs: recentBreadcrumbs(),
			// Attribution FIRST: `counters` is bounded at 600 chars and truncated from the tail, and a
			// ten-counter snapshot can reach that on its own — which would drop the fixed-width
			// rewriteCheck line exactly on the richest reports, the ones it exists to explain. With
			// neither part present the field is omitted, not sent as ''.
			counters: [rewrite?.attribution, counterSnapshot()].filter(Boolean).join(' || ') || undefined,
			// What actually differed. React reports `args[]=text` with an empty diff in production, so
			// without this a text mismatch says only that *some* text changed — which is why this family
			// has been diagnosed from guesses rather than from data.
			mismatch: rewrite?.mismatch,
		}),
	}).catch((logErr) => {
		console.error('Failed to log error to backend:', logErr);
	});
}

function reportRecoverableError(error: unknown, errorInfo: { componentStack?: string | null }): void {
	reportWithComponentStack(
		(message) => (isHydrationError(message) ? '[HYDRATION]' : '[RECOVERABLE]'),
		error,
		errorInfo,
	);
}

/**
 * Report the errors React does NOT recover from and no boundary caught.
 *
 * Without this they take React's default `onUncaughtError`, which calls `reportError(error)` and so
 * surfaces as a window `error` event — picked up by the global handler in `router.tsx`, which has no
 * access to React's `errorInfo` and therefore reports **no component stack**. That is exactly why
 * TEC-3690 (`Minified React error #418 … args[]=HTML`, a structural hydration mismatch) arrived
 * undiagnosable: React-internal frames in `vendor-react-*.js` and nothing naming our own elements.
 * Sourcemaps cannot help there, because the frames genuinely belong to the reconciler.
 *
 * Supplying this handler replaces that `reportError` call, so the failure is reported ONCE — here, with
 * the component stack — rather than once from the window handler without it.
 *
 * Kept under its own `[UNCAUGHT]` prefix rather than reusing `[HYDRATION]`: the system-error reporter
 * fingerprints on the message prefix, and an error React could not recover from is a different severity
 * from one it patched over. Merging them would hide a broken page behind a ticket for a cosmetic
 * mismatch.
 */
function reportUncaughtError(error: unknown, errorInfo: { componentStack?: string | null }): void {
	reportWithComponentStack(() => '[UNCAUGHT]', error, errorInfo);
}

// Outside the transition and before it: every rewrite the monitor logs from here on could be React's
// own, and one logged earlier provably could not. See `HYDRATION_STARTED_KEY`.
markHydrationStarted();

startTransition(() => {
	hydrateRoot(
		document,
		<StrictMode>
			<StartClient />
		</StrictMode>,
		// `onCaughtError` is deliberately left at React's default: an error a boundary caught is already
		// reported by that boundary (see `DefaultCatchBoundary`), so handling it here would double-report.
		{ onRecoverableError: reportRecoverableError, onUncaughtError: reportUncaughtError },
	);
});
